import React, { useEffect, useState } from 'react';
import axios from 'axios';
import MathText from '../components/MathText';

const API = process.env.REACT_APP_API_URL || '';
const PAGE_SIZE = 20;

export default function Audit1() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [page, setPage]           = useState(0); // 0-indexed

  useEffect(() => {
    axios.get(`${API}/api/audit1/questions`)
      .then(res => {
        console.log('[Audit1] loaded', res.data.length, 'questions');
        setQuestions(Array.isArray(res.data) ? res.data : []);
        setLoading(false);
      })
      .catch(err => {
        console.error('[Audit1] fetch error', err);
        setError(err?.response?.data?.error || err.message || 'Unknown error');
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--cb-gray-500)' }}>
        Loading questions…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--cb-red)' }}>
        <strong>Error loading questions:</strong> {error}
      </div>
    );
  }

  const totalPages  = Math.ceil(questions.length / PAGE_SIZE);
  const pageStart   = page * PAGE_SIZE;
  const pageEnd     = Math.min(pageStart + PAGE_SIZE, questions.length);
  const pageQs      = questions.slice(pageStart, pageEnd);

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '28px 20px 80px' }}>

      {/* ── Page header ── */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--cb-gray-900)', marginBottom: 4 }}>
          AUDIT1 — All Questions (questions1.js)
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--cb-gray-500)', fontSize: 14 }}>
            {questions.length} total questions · showing {pageStart + 1}–{pageEnd}
          </span>
          <span style={{
            background: 'var(--cb-red-light)',
            color: 'var(--cb-red)',
            borderRadius: 6,
            padding: '2px 10px',
            fontSize: 12,
            fontWeight: 600,
          }}>
            TEMPORARY — delete after audit
          </span>
        </div>
      </div>

      {/* ── Pagination (top) ── */}
      <Pagination page={page} totalPages={totalPages} onChange={setPage} />

      {/* ── Questions ── */}
      {pageQs.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--cb-gray-500)' }}>
          No questions found. (Total loaded: {questions.length})
        </div>
      ) : (
        pageQs.map((q, i) => (
          <QuestionCard key={q.id} q={q} globalIdx={pageStart + i} />
        ))
      )}

      {/* ── Pagination (bottom) ── */}
      <Pagination page={page} totalPages={totalPages} onChange={p => { setPage(p); window.scrollTo(0, 0); }} />
    </div>
  );
}

/* ─── Pagination component ─────────────────────────────────────────────── */
function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '16px 0', flexWrap: 'wrap' }}>
      <button
        onClick={() => onChange(page - 1)}
        disabled={page === 0}
        style={btnStyle(page === 0)}
      >
        ← Prev
      </button>
      {Array.from({ length: totalPages }, (_, i) => (
        <button
          key={i}
          onClick={() => onChange(i)}
          style={btnStyle(false, i === page)}
        >
          {i + 1}
        </button>
      ))}
      <button
        onClick={() => onChange(page + 1)}
        disabled={page === totalPages - 1}
        style={btnStyle(page === totalPages - 1)}
      >
        Next →
      </button>
    </div>
  );
}

function btnStyle(disabled, active = false) {
  return {
    padding: '6px 14px',
    borderRadius: 6,
    border: active ? '2px solid var(--cb-blue)' : '1px solid var(--cb-gray-300)',
    background: active ? 'var(--cb-blue)' : 'var(--cb-white)',
    color: active ? '#fff' : disabled ? 'var(--cb-gray-500)' : 'var(--cb-gray-900)',
    fontWeight: active ? 700 : 400,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    fontSize: 14,
  };
}

/* ─── Single question card ─────────────────────────────────────────────── */
function QuestionCard({ q, globalIdx }) {
  return (
    <div style={{
      background: 'var(--cb-white)',
      border: '1px solid var(--cb-gray-200)',
      borderRadius: 12,
      padding: '20px 22px',
      marginBottom: 24,
      boxShadow: 'var(--shadow-sm)',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, fontSize: 17, color: 'var(--cb-gray-900)', minWidth: 32 }}>
          #{globalIdx + 1}
        </span>
        <code style={{
          background: 'var(--cb-gray-100)',
          color: 'var(--cb-gray-700)',
          padding: '2px 8px',
          borderRadius: 5,
          fontSize: 12,
        }}>
          {q.id}
        </code>
        {q.section && (
          <span style={{
            background: q.section === 'Math' ? 'var(--cb-blue-light)' : 'var(--cb-green-light)',
            color: q.section === 'Math' ? 'var(--cb-blue)' : 'var(--cb-green)',
            padding: '2px 8px',
            borderRadius: 5,
            fontSize: 12,
            fontWeight: 600,
          }}>
            {q.section}
          </span>
        )}
        {q.domain && (
          <span style={{
            background: 'var(--cb-gray-100)',
            color: 'var(--cb-gray-700)',
            padding: '2px 8px',
            borderRadius: 5,
            fontSize: 12,
            fontWeight: 600,
          }}>
            {q.domain}
          </span>
        )}
        {q.isFreeResponse && (
          <span style={{
            background: 'var(--cb-yellow-light)',
            color: 'var(--cb-yellow)',
            padding: '2px 8px',
            borderRadius: 5,
            fontSize: 11,
            fontWeight: 600,
          }}>
            Free Response
          </span>
        )}
      </div>

      {/* Passage/stimulus */}
      {q.passage && (
        <div style={{
          background: 'var(--cb-gray-100)',
          border: '1px solid var(--cb-gray-200)',
          borderRadius: 8,
          padding: '12px 14px',
          marginBottom: 14,
          fontSize: 14,
          lineHeight: 1.7,
          color: 'var(--cb-gray-900)',
          whiteSpace: 'pre-wrap',
        }}>
          <MathText text={q.passage} />
        </div>
      )}

      {/* Figures */}
      {q.figures && q.figures.length > 0
        ? q.figures.map((fig, i) => (
            <img
              key={i}
              src={`/figures/${fig}`}
              alt={`Figure ${i + 1} for ${q.id}`}
              onError={e => { e.target.style.outline = '2px solid red'; e.target.alt = `MISSING: /figures/${fig}`; }}
              style={{ maxWidth: '100%', marginBottom: 12, borderRadius: 8, border: '1px solid var(--cb-gray-200)', display: 'block' }}
            />
          ))
        : q.figure
          ? (
            <img
              src={`/figures/${q.figure}`}
              alt={`Figure for ${q.id}`}
              onError={e => { e.target.style.outline = '2px solid red'; e.target.alt = `MISSING: /figures/${q.figure}`; }}
              style={{ maxWidth: '100%', marginBottom: 14, borderRadius: 8, border: '1px solid var(--cb-gray-200)', display: 'block' }}
            />
          )
          : null}

      {/* Question text */}
      <div style={{
        fontSize: 15,
        lineHeight: 1.7,
        color: 'var(--cb-gray-900)',
        marginBottom: 16,
        fontWeight: 500,
      }}>
        <MathText text={q.question} />
      </div>

      {/* Choices */}
      {!q.choices ? (
        <div style={{
          padding: '10px 14px',
          borderRadius: 8,
          border: '1px solid var(--cb-gray-200)',
          background: 'var(--cb-gray-100)',
          fontSize: 14,
          color: 'var(--cb-gray-700)',
          marginBottom: 14,
        }}>
          <strong>Free Response Answer:</strong> {q.freeResponseAnswer ?? '—'}
        </div>
      ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 14 }}>
        {Object.entries(q.choices).map(([letter, text]) => {
          const isCorrect = letter === q.correctAnswer;
          return (
            <div
              key={letter}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '9px 14px',
                borderRadius: 8,
                border: isCorrect
                  ? '1.5px solid var(--cb-green)'
                  : '1px solid var(--cb-gray-200)',
                background: isCorrect ? 'var(--cb-green-light)' : 'var(--cb-gray-100)',
                fontSize: 14,
                lineHeight: 1.5,
              }}
            >
              <span style={{
                fontWeight: 700,
                fontSize: 14,
                color: isCorrect ? 'var(--cb-green)' : 'var(--cb-gray-700)',
                minWidth: 18,
                paddingTop: 1,
              }}>
                {letter}
              </span>
              <span style={{ color: 'var(--cb-gray-900)' }}><MathText text={text} /></span>
              {isCorrect && (
                <span style={{ marginLeft: 'auto', color: 'var(--cb-green)', fontWeight: 700, fontSize: 13 }}>✓</span>
              )}
            </div>
          );
        })}
      </div>
      )}

      {/* Explanation */}
      {q.explanation && (
        <div style={{
          fontSize: 13,
          color: 'var(--cb-gray-500)',
          borderTop: '1px solid var(--cb-gray-200)',
          paddingTop: 10,
          fontStyle: 'italic',
        }}>
          <strong style={{ fontStyle: 'normal', color: 'var(--cb-gray-700)' }}>Explanation: </strong>
          {q.explanation}
        </div>
      )}
    </div>
  );
}
